import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowLeft, ClipboardList, Library, Package, RefreshCw, Target, Rocket } from 'lucide-react';
import { labApi } from '../lib/api';
import { calculateProductEconomics, normalizeBriefEconomics, parseProductEconomics } from '@/lib/product-economics';
import { EmptyState } from '../components/EmptyState';
import { Button } from '../components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/Tabs';
import type { LabContent, LabProduct } from './lab-dashboard/types';
import { CompetitorSection } from './lab-dashboard/CompetitorSection';
import { SupplierSection } from './lab-dashboard/SupplierSection';
import { ContentHub } from './lab-dashboard/ContentHub';
import {
  ProductSourcesPanel,
  type LabProductFieldConfig,
  type LabProductFieldOption,
} from './lab-dashboard/ProductSourcesPanel';
import { GenerationPanel } from './lab-dashboard/GenerationPanel';
import { InputPanel, MULTI_SEP, type SourceCheckState, type UserInputField } from './lab-dashboard/InputPanel';
import { NewKbModal } from './lab-dashboard/NewKbModal';
import { KbPickerModal } from './lab-dashboard/KbPickerModal';
import { VideoStrategyPickerModal, type VideoStructureOption } from './lab-dashboard/VideoStrategyPickerModal';
import { StaticStrategyPickerModal, type StaticCategoryOption, type StaticSchemaOption, type StaticStrategyOption, type StaticStrategySelection } from './lab-dashboard/StaticStrategyPickerModal';
import { SocialStrategyPickerModal, type SocialStrategyOption } from './lab-dashboard/SocialStrategyPickerModal';
import { DeleteConfirmModal } from './lab-dashboard/DeleteConfirmModal';
import { GenerateAllModal } from './lab-dashboard/GenerateAllModal';
import { BatchGenerateModal } from './lab-dashboard/BatchGenerateModal';
import { ProductPageGenerateModal } from './lab-dashboard/ProductPageGenerateModal';
import { getProductImagesRunState, getProductImagesRunFailure } from './lab-dashboard/generationRunState';
import {
  getKnowledgePipelineFailure,
  hasKnowledgePipelineProgress,
} from './lab-dashboard/knowledgeGenerationState';

interface UserInputScreen {
  key: string;
  fields: UserInputField[];
}

type DashboardTab = 'assets' | 'radar' | 'suppliers' | 'sources';
type InputPanelMode = 'knowledge' | 'content';
type KnowledgeSourceName = 'advertorial' | 'landing';

const LAB_VIEW_PARAM = 'labView';
const LAB_CONTENT_TAB_PARAM = 'labTab';

interface PendingContentGeneration {
  type: string;
  kbId: string;
  initialInputs?: Record<string, string>;
  strategy?: string;
  structure?: string | null;
  selectedSchemas?: string[];
  carouselSchema?: string | null;
  pipeline?: string;
  autoChain?: boolean;
  production?: { voiceId?: string; avatarId?: string; voiceSource?: 'heygen' | 'elevenlabs' } | null;
}

export interface LabVideoStrategyOption {
  key: string;
  label: string;
  description: string;
  pipeline: string;
  productionProfile: string;
}

function pipelineForContentType(type: string): string {
  return type === 'AD_GRAPHIC' ? 'AD_CREATIVE' : type;
}

function contentPipelineState(content: LabContent): { status: string; pipeline: string } | null {
  const state = content.sections?.__pipeline_state;
  const status = state?.original;
  if (status !== 'generating' && status !== 'cancelled') return null;
  return { status, pipeline: state?.pipeline || pipelineForContentType(content.type) };
}

function isDashboardTab(value: string): value is DashboardTab {
  return value === 'assets' || value === 'radar' || value === 'suppliers' || value === 'sources';
}

function dashboardTabFromSearch(): DashboardTab {
  if (typeof window === 'undefined') return 'assets';
  const value = new URLSearchParams(window.location.search).get(LAB_VIEW_PARAM);
  return value && isDashboardTab(value) ? value : 'assets';
}

function contentHubTabFromSearch(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get(LAB_CONTENT_TAB_PARAM);
}

export function LabProductDashboardPage() {
  const { productId } = useParams<{ productId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState<LabProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Inline generation state
  const [generatingKb, setGeneratingKb] = useState(false);
  const [generatingKbName, setGeneratingKbName] = useState('');
  const [generatingKbId, setGeneratingKbId] = useState<string | null>(null);
  const [knowledgeGenerationError, setKnowledgeGenerationError] = useState<string | null>(null);
  const [isFirstGeneration, setIsFirstGeneration] = useState(false);
  const [userInputs, setUserInputs] = useState<Record<string, string> | undefined>();
  const [restartKnowledgeGeneration, setRestartKnowledgeGeneration] = useState(false);
  const [retryingKnowledgeGeneration, setRetryingKnowledgeGeneration] = useState(false);
  const autoGenTriggered = useRef(false);
  const wizardHistoryPushed = useRef(false);
  const productPageModalHistoryPushed = useRef(false);
  const dismissedContentTargetRef = useRef<string | null>(null);

  // Input panel / wizard state
  const [showInputPanel, setShowInputPanel] = useState(false);
  const [inputPanelFields, setInputPanelFields] = useState<UserInputField[]>([]);
  const [inputPanelScreens, setInputPanelScreens] = useState<UserInputScreen[]>([]);
  const [inputPanelMode, setInputPanelMode] = useState<InputPanelMode>('knowledge');
  const [pendingContentGeneration, setPendingContentGeneration] = useState<PendingContentGeneration | null>(null);
  const [resumingGeneration, setResumingGeneration] = useState(false);
  const [resumingContentGeneration, setResumingContentGeneration] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardValues, setWizardValues] = useState<Record<string, string>>({});
  const [sourceChecks, setSourceChecks] = useState<Record<string, SourceCheckState>>({});
  const sourceCheckRequestsRef = useRef<Record<string, string>>({});

  // Content generation panel state
  const [generatingContentType, setGeneratingContentType] = useState<string | null>(null);
  const [generatingContentId, setGeneratingContentId] = useState<string | null>(null);

  // Modals
  const [showNewKbModal, setShowNewKbModal] = useState(false);
  const [kbPickerForContent, setKbPickerForContent] = useState<{ type: string; initialInputs?: Record<string, string>; strategy?: string; structure?: string | null; selectedSchemas?: string[]; carouselSchema?: string | null; pipeline?: string; autoChain?: boolean; production?: { voiceId?: string; avatarId?: string; voiceSource?: 'heygen' | 'elevenlabs' } | null } | null>(null);
  const [videoStrategyPicker, setVideoStrategyPicker] = useState<{ kbIds: string[] } | null>(null);
  const [videoStrategies, setVideoStrategies] = useState<LabVideoStrategyOption[]>([]);
  const [videoStructures, setVideoStructures] = useState<VideoStructureOption[]>([]);
  const [staticStrategyPicker, setStaticStrategyPicker] = useState<{ initialInputs?: Record<string, string> } | null>(null);
  const [socialStrategyPicker, setSocialStrategyPicker] = useState<{ initialInputs?: Record<string, string> } | null>(null);
  const [socialStrategies, setSocialStrategies] = useState<SocialStrategyOption[]>([]);
  const [staticStrategies, setStaticStrategies] = useState<StaticStrategyOption[]>([]);
  const [staticSchemas, setStaticSchemas] = useState<StaticSchemaOption[]>([]);
  const [staticCategories, setStaticCategories] = useState<StaticCategoryOption[]>([]);
  const [generatingStrategyPipeline, setGeneratingStrategyPipeline] = useState<string | null>(null);
  const [generatingContent, setGeneratingContent] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'kb' | 'content'; id: string } | null>(null);
  const [removeLaboratoryConfirm, setRemoveLaboratoryConfirm] = useState(false);
  const [removingLaboratory, setRemovingLaboratory] = useState(false);
  const [removeLaboratoryError, setRemoveLaboratoryError] = useState<string | null>(null);
  const [showGenerateAll, setShowGenerateAll] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [showProductPageGenerateModal, setShowProductPageGenerateModal] = useState(false);
  const [contentHubTabToken, setContentHubTabTokenState] = useState<string | null>(() => contentHubTabFromSearch());
  const [dashboardTab, setDashboardTab] = useState<DashboardTab>(() => {
    return dashboardTabFromSearch();
  });
  const [productFields, setProductFields] = useState<LabProductFieldConfig[]>([]);
  const [productFieldOptions, setProductFieldOptions] = useState<Record<string, LabProductFieldOption[]>>({});

  const buildInputScreens = (fields: UserInputField[], pipeline?: string): UserInputScreen[] => {
    const screens: UserInputScreen[] = [];
    for (const inputField of fields) {
      const screenKey = pipeline === 'QUIZ'
        ? 'quiz-config'
        : inputField.screenKey || `field:${inputField.name}`;
      const last = screens[screens.length - 1];
      if (last && last.key === screenKey) {
        last.fields.push(inputField);
      } else {
        screens.push({ key: screenKey, fields: [inputField] });
      }
    }
    return screens;
  };

  const optionValue = (option: NonNullable<UserInputField['options']>[number]): string => (
    typeof option === 'string' ? option : option.value
  );

  const defaultWizardValueForField = (field: UserInputField): string => {
    if (field.defaultValue) return field.defaultValue;
    if (field.inputType !== 'select') return '';
    const firstOption = field.options?.find(option => optionValue(option));
    return firstOption ? optionValue(firstOption) : '';
  };

  const buildDefaultWizardValues = (fields: UserInputField[]): Record<string, string> => {
    const values: Record<string, string> = {};
    for (const field of fields) {
      const defaultValue = defaultWizardValueForField(field);
      if (defaultValue) values[field.name] = defaultValue;
    }
    return values;
  };

  const matchesVisibilityValue = (currentValue: string, expected?: string | string[]): boolean => {
    if (expected === undefined) return true;
    const expectedValues = Array.isArray(expected) ? expected : [expected];
    return expectedValues.includes(currentValue);
  };

  const isInputFieldVisible = (field: UserInputField, values: Record<string, string>): boolean => {
    if (field.fallbackForSource) {
      const alreadyProvided = Boolean((values[field.name] || '').trim());
      if (!alreadyProvided && sourceChecks[field.fallbackForSource]?.status !== 'failed') return false;
    }
    if (!field.visibleWhen || field.visibleWhen.length === 0) return true;
    return field.visibleWhen.every(condition => {
      const currentValue = values[condition.field] || '';
      if (condition.equals !== undefined && !matchesVisibilityValue(currentValue, condition.equals)) return false;
      if (condition.notEquals !== undefined && matchesVisibilityValue(currentValue, condition.notEquals)) return false;
      return true;
    });
  };

  const visibleInputScreens = (screens: UserInputScreen[], values: Record<string, string>): UserInputScreen[] => (
    screens
      .map(screen => ({
        ...screen,
        fields: screen.fields.filter(field => isInputFieldVisible(field, values)),
      }))
      .filter(screen => screen.fields.length > 0)
  );

  const stringifyProductValue = (value: unknown): string => {
    if (value == null) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return JSON.stringify(value);
  };

  const valueAtPath = (source: Record<string, unknown>, path: string): unknown => {
    return path.split('.').reduce<unknown>((current, part) => {
      if (!current || typeof current !== 'object' || Array.isArray(current)) return undefined;
      return (current as Record<string, unknown>)[part];
    }, source);
  };

  const sanitizedCustomFields = (customFields: Record<string, unknown>): Record<string, unknown> => {
    return Object.fromEntries(
      Object.entries(customFields).filter(([key]) => !['_fieldDuplicates', 'marketplaceLink', 'wholesale'].includes(key)),
    );
  };

  const productValuesForMapping = (productData: Record<string, unknown>, fieldKey: string): string[] => {
    const customFields = productData.customFields && typeof productData.customFields === 'object'
      ? productData.customFields as Record<string, unknown>
      : {};
    const duplicateMap = customFields._fieldDuplicates && typeof customFields._fieldDuplicates === 'object'
      ? customFields._fieldDuplicates as Record<string, unknown>
      : {};
    const values: string[] = [];
    const mappingKeys = fieldKey.split(',').map(key => key.trim()).filter(Boolean);

    for (const mappingKey of mappingKeys) {
      if (mappingKey === 'marketplaceLink') {
        const primary = stringifyProductValue(productData.primaryMarketplaceUrl);
        if (primary.trim()) values.push(primary);
        continue;
      }
      if (mappingKey === 'wholesale' || mappingKey === 'customFields.wholesale') {
        const primary = stringifyProductValue(productData.primaryWholesaleUrl);
        if (primary.trim()) values.push(primary);
        continue;
      }
      const raw = mappingKey.includes('.')
        ? valueAtPath(productData, mappingKey)
        : mappingKey === 'customFields'
          ? sanitizedCustomFields(customFields)
          : productData[mappingKey] ?? customFields[mappingKey];

      if (Array.isArray(raw)) {
        values.push(...raw.map(stringifyProductValue));
      } else if (raw != null) {
        values.push(stringifyProductValue(raw));
      }

      const duplicateKey = mappingKey.startsWith('customFields.')
        ? mappingKey.slice('customFields.'.length)
        : mappingKey;
      const duplicates = duplicateMap[duplicateKey];
      if (Array.isArray(duplicates)) {
        values.push(...duplicates.map(stringifyProductValue));
      }
    }

    return values.map(v => v.trim()).filter(Boolean);
  };

  const normalizePackageWizardValue = (value: string): string => {
    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) return value.trim();
      return parsed
        .map((item, index) => {
          const row = item && typeof item === 'object' ? item as Record<string, unknown> : {};
          const name = String(row.name || `Pakiet ${index + 1}`).trim();
          const oldPrice = String(row.oldPrice || 'BRAK DANYCH').trim();
          const newPrice = String(row.newPrice || 'BRAK DANYCH').trim();
          const economics = parseProductEconomics(row.economics);
          const costs = calculateProductEconomics(economics);
          const composition = row.economics ? `\n- Liczba sprzedawanych sztuk: ${economics.saleQuantity}${costs ? `\n- Koszt zakupu pakietu: ${costs.costPerSaleUnit} zł` : ''}` : '';
          return `${name}\n- Cena standardowa: ${oldPrice}\n- Cena promocyjna: ${newPrice}${composition}`;
        })
        .join('\n\n');
    } catch {
      return value.trim();
    }
  };

  const normalizeWizardValue = (value: string, field?: UserInputField): string => {
    if (field?.inputType === 'packages') return normalizePackageWizardValue(value);
    if (!value.includes(MULTI_SEP)) return value.trim();
    return value
      .split(MULTI_SEP)
      .map(v => v.trim())
      .filter(Boolean)
      .map((v, index) => `Wartość ${index + 1}:\n${v}`)
      .join('\n\n');
  };

  const normalizeWizardValues = (values: Record<string, string>): Record<string, string> => {
    const fieldsByName = new Map(inputPanelFields.map(field => [field.name, field]));
    return Object.fromEntries(
      Object.entries(values)
        .map(([key, value]) => [key, normalizeWizardValue(value, fieldsByName.get(key))])
        .filter(([, value]) => Boolean(value)),
    );
  };

  const wait = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms));

  const updateLabUrlState = useCallback((updates: Record<string, string | null>) => {
    const next = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);
    for (const [key, value] of Object.entries(updates)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  const handleDashboardTabChange = useCallback((value: string) => {
    if (!isDashboardTab(value)) return;
    setDashboardTab(value);
    updateLabUrlState({ [LAB_VIEW_PARAM]: value === 'assets' ? null : value });
  }, [updateLabUrlState]);

  const handleContentHubTabTokenChange = useCallback((token: string | null) => {
    setContentHubTabTokenState(token);
    updateLabUrlState({ [LAB_CONTENT_TAB_PARAM]: token && token !== 'kb' ? token : null });
  }, [updateLabUrlState]);

  // Load product data
  const loadProduct = async (): Promise<LabProduct | null> => {
    if (!productId) return null;
    try {
      const res = await labApi.getProduct(productId);
      const loadedProduct = res.data.data as LabProduct;
      setProduct(loadedProduct);
      const incompleteKb = loadedProduct.knowledgeBases?.find(kb => !kb.isComplete);
      const persistedError = getKnowledgePipelineFailure(incompleteKb);
      if (persistedError) setKnowledgeGenerationError(persistedError);
      if (loadedProduct.knowledgeBases?.some(kb => kb.isComplete)) setKnowledgeGenerationError(null);
      return loadedProduct;
    } catch (err) {
      setError('Nie udało się załadować produktu.');
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const waitForCompleteKnowledgeBase = async (): Promise<boolean> => {
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const loadedProduct = await loadProduct();
      if (loadedProduct?.knowledgeBases?.some(kb => kb.isComplete)) return true;
      await wait(750);
    }
    return false;
  };

  useEffect(() => {
    loadProduct();
  }, [productId]);

  useEffect(() => {
    if (!showInputPanel) return;
    if (!wizardHistoryPushed.current && typeof window !== 'undefined') {
      window.history.pushState({ ...(window.history.state || {}), ecomflowLabWizard: true }, '', window.location.href);
      wizardHistoryPushed.current = true;
    }

    const handlePopState = () => {
      if (!wizardHistoryPushed.current) return;
      wizardHistoryPushed.current = false;
      setShowInputPanel(false);
      setPendingContentGeneration(null);
      setWizardStep(0);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showInputPanel]);

  useEffect(() => {
    if (!showProductPageGenerateModal) return;
    if (!productPageModalHistoryPushed.current && typeof window !== 'undefined') {
      window.history.pushState({ ...(window.history.state || {}), ecomflowLabProductPageModal: true }, '', window.location.href);
      productPageModalHistoryPushed.current = true;
    }

    const handlePopState = () => {
      if (!productPageModalHistoryPushed.current) return;
      productPageModalHistoryPushed.current = false;
      setShowProductPageGenerateModal(false);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showProductPageGenerateModal]);

  useEffect(() => {
    const screens = visibleInputScreens(inputPanelScreens, wizardValues);
    if (screens.length > 0 && wizardStep > screens.length - 1) {
      setWizardStep(screens.length - 1);
    }
  }, [inputPanelScreens, wizardValues, wizardStep]);

  useEffect(() => {
    if (!showInputPanel || inputPanelMode !== 'knowledge' || !productId) return;

    const activeScreens = visibleInputScreens(inputPanelScreens, wizardValues);
    const currentScreen = activeScreens[wizardStep];
    const configuredScreen = currentScreen
      ? inputPanelScreens.find(screen => screen.key === currentScreen.key)
      : undefined;
    const candidates = new Map<string, { sourceName: KnowledgeSourceName; rawValue: string }>();

    for (const inputField of configuredScreen?.fields || []) {
      if (inputField.inputType !== 'link') continue;
      if (!['advertorial', 'landing'].includes(inputField.name)) continue;
      candidates.set(inputField.name, {
        sourceName: inputField.name as KnowledgeSourceName,
        rawValue: wizardValues[inputField.name] || '',
      });
    }

    const timer = window.setTimeout(() => {
      for (const [sourceKey, candidate] of candidates) {
        const rawUrls = candidate.rawValue
          .split(MULTI_SEP)
          .map(value => value.trim())
          .filter(Boolean);
        if (rawUrls.length === 0) {
          delete sourceCheckRequestsRef.current[sourceKey];
          setSourceChecks(prev => {
            if (!prev[sourceKey]) return prev;
            const next = { ...prev };
            delete next[sourceKey];
            return next;
          });
          continue;
        }

        const signature = rawUrls.join('|');
        if (sourceCheckRequestsRef.current[sourceKey] === signature) continue;
        sourceCheckRequestsRef.current[sourceKey] = signature;

        const invalidUrl = rawUrls.find(rawUrl => {
          try {
            const parsed = new URL(rawUrl);
            return parsed.protocol !== 'http:' && parsed.protocol !== 'https:';
          } catch {
            return true;
          }
        });
        if (invalidUrl) {
          setSourceChecks(prev => ({
            ...prev,
            [sourceKey]: {
              status: 'invalid',
              checked: 0,
              total: rawUrls.length,
              error: 'Wpisz pełny adres zaczynający się od https:// albo usuń niepoprawny link.',
            },
          }));
          continue;
        }

        setSourceChecks(prev => ({
          ...prev,
          [sourceKey]: { status: 'checking', checked: 0, total: rawUrls.length },
        }));

        void Promise.all(rawUrls.map(url => (
          labApi.checkKnowledgeSource(productId, { sourceName: candidate.sourceName, url })
            .then(response => response.data.data as {
              status: 'completed' | 'failed';
              preview?: string;
              error?: string;
            })
        ))).then(results => {
          if (sourceCheckRequestsRef.current[sourceKey] !== signature) return;
          const failed = results.find(result => result.status === 'failed');
          setSourceChecks(prev => ({
            ...prev,
            [sourceKey]: failed
              ? {
                  status: 'failed',
                  checked: results.filter(result => result.status === 'completed').length,
                  total: results.length,
                  error: failed.error || 'Strona nie zwróciła użytecznej treści.',
                }
              : {
                  status: 'completed',
                  checked: results.length,
                  total: results.length,
                  preview: results.find(result => result.preview)?.preview,
                },
          }));
        }).catch(error => {
          if (sourceCheckRequestsRef.current[sourceKey] !== signature) return;
          const message = error && typeof error === 'object' && 'response' in error
            ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
            : undefined;
          setSourceChecks(prev => ({
            ...prev,
            [sourceKey]: {
              status: 'failed',
              checked: 0,
              total: rawUrls.length,
              error: message || 'Nie udało się sprawdzić źródła. Wklej jego treść ręcznie albo usuń link.',
            },
          }));
        });
      }
    }, 500);

    return () => window.clearTimeout(timer);
  }, [showInputPanel, inputPanelMode, inputPanelScreens, wizardStep, wizardValues, productId]);

  useEffect(() => {
    labApi.getFieldConfig()
      .then(res => {
        const data = res.data.data;
        setProductFields(data.fields || []);
        setProductFieldOptions(data.fieldOptions || {});
      })
      .catch(err => {
        console.error('Nie udało się załadować konfiguracji pól produktu:', err);
      });
  }, []);

  // Check pipeline config for user_input fields
  const checkPipelineInputs = async (pipeline = 'KNOWLEDGE'): Promise<{ hasInputs: boolean; fields: UserInputField[] }> => {
    try {
      const res = await labApi.getPipeline(pipeline);
      const config = res.data.data;
      const items = config?.items || [];
      const userInputItems = items
        .filter((i: { type: string }) => i.type === 'user_input')
        .map((i: UserInputField & Record<string, unknown>) => ({
          ...i,
          panelTitle: i.panelTitle || config?.inputPanelTitle || '',
          panelDescription: i.panelDescription || config?.inputPanelDescription || '',
          ctaLabel: i.ctaLabel || config?.ctaLabel || (pipeline === 'KNOWLEDGE' ? '' : 'Generuj materiał'),
          backLabel: i.backLabel || config?.backLabel || '',
        })) as UserInputField[];
      return { hasInputs: userInputItems.length > 0, fields: userInputItems };
    } catch {
      return { hasInputs: false, fields: [] };
    }
  };

  // Build initial wizard values by auto-filling from product data
  const buildAutoFillValues = (fields: UserInputField[]): Record<string, string> => {
    if (!product) return {};
    const values: Record<string, string> = {};
    const productData = product as unknown as Record<string, unknown>;
    for (const field of fields) {
      if (field.name === 'product_unit_cost' && Array.isArray(product.pricePackages)) {
        const firstPackage = product.pricePackages[0] as { economics?: unknown } | undefined;
        if (firstPackage?.economics) { values[field.name] = JSON.stringify(firstPackage.economics); continue; }
      }
      if (!field.autoFillFrom) continue;
      if (field.autoFillFrom === 'canonicalProductMaterial') {
        const customFields = product.customFields && typeof product.customFields === 'object' && !Array.isArray(product.customFields)
          ? product.customFields as Record<string, unknown>
          : {};
        const parts = [product.description, stringifyProductValue(customFields.productFacts)]
          .map(value => typeof value === 'string' ? value.trim() : '')
          .filter(Boolean);
        const uniqueParts = [...new Set(parts)];
        if (uniqueParts.length > 0) values[field.name] = uniqueParts.join('\n\n');
        continue;
      }
      if (field.inputType === 'packages' && field.autoFillFrom === 'pricePackages' && Array.isArray(product.pricePackages)) {
        values[field.name] = JSON.stringify(product.pricePackages);
        continue;
      }
      const mappedValues = productValuesForMapping(productData, field.autoFillFrom);
      if (mappedValues.length === 0) continue;
      values[field.name] = field.allowMultiple
        ? mappedValues.join(MULTI_SEP)
        : mappedValues.join('\n\n');
    }
    return values;
  };

  const buildInitialWizardValues = (fields: UserInputField[], initialInputs?: Record<string, string>): Record<string, string> => ({
    ...buildDefaultWizardValues(fields),
    ...buildAutoFillValues(fields),
    ...(initialInputs || {}),
  });

  const startFirstKnowledgeBaseInstaller = async (kbName = 'Główna Baza Wiedzy', restart = false) => {
    setKnowledgeGenerationError(null);
    setSourceChecks({});
    sourceCheckRequestsRef.current = {};
    setInputPanelMode('knowledge');
    setPendingContentGeneration(null);
    setIsFirstGeneration(true);
    setGeneratingKbName(kbName);
    setRestartKnowledgeGeneration(restart);

    const result = await checkPipelineInputs();
    if (result.hasInputs) {
      setInputPanelFields(result.fields);
      setInputPanelScreens(buildInputScreens(result.fields));
      setWizardStep(0);
      setWizardValues(buildInitialWizardValues(result.fields));
      setSearchParams({}, { replace: true });
      setShowInputPanel(true);
    } else {
      setSearchParams({}, { replace: true });
      setGeneratingKb(true);
    }
  };

  // Auto-start first KB installer when entering a lab without a completed KB.
  useEffect(() => {
    if (autoGenTriggered.current) return;
    if (!product || loading || generatingKb || showInputPanel || generatingContentType) return;
    const hasCompleteKb = product.knowledgeBases?.some(kb => kb.isComplete);
    const forceFreshStart = searchParams.get('autoGenerate') === 'true';
    if (hasCompleteKb) {
      if (forceFreshStart) setSearchParams({}, { replace: true });
      return;
    }

    const incompleteKb = product.knowledgeBases?.find(kb => !kb.isComplete);
    if (getKnowledgePipelineFailure(incompleteKb)) return;
    if (incompleteKb && hasKnowledgePipelineProgress(incompleteKb) && !forceFreshStart) return;
    if (product.status === 'ANALYZING' && incompleteKb && !forceFreshStart) return;

    autoGenTriggered.current = true;
    startFirstKnowledgeBaseInstaller(
      forceFreshStart ? 'Główna Baza Wiedzy' : (incompleteKb?.name || 'Główna Baza Wiedzy'),
      forceFreshStart || Boolean(incompleteKb),
    );
  }, [product, loading, generatingKb, showInputPanel, generatingContentType, searchParams]);

  // Resume generation if product is ANALYZING with an incomplete KB
  useEffect(() => {
    if (!product || loading || generatingKb || showInputPanel) return;
    if (autoGenTriggered.current) return;
    if (searchParams.get('autoGenerate') === 'true') return;

    const incompleteKb = product.knowledgeBases?.find(kb => !kb.isComplete);
    if (incompleteKb && !getKnowledgePipelineFailure(incompleteKb)
      && (product.status === 'ANALYZING' || hasKnowledgePipelineProgress(incompleteKb))) {
      setGeneratingKbName(incompleteKb.name || 'Baza Wiedzy');
      setIsFirstGeneration(false);
      setResumingGeneration(true);
      setGeneratingKb(true);
    }
  }, [product, loading, generatingKb, showInputPanel, searchParams]);

  // Recover image or text generation after refresh, or when the text phase finishes.
  useEffect(() => {
    if (!product || loading || generatingKb || showInputPanel || generatingContentType) return;
    const imageContent = product.contents.find(content => {
      const state = getProductImagesRunState(content);
      return (state?.status === 'preparing' || state?.status === 'generating') && !getProductImagesRunFailure(content);
    });
    if (imageContent && dismissedContentTargetRef.current !== imageContent.id) {
      setGeneratingContent('PRODUCT_IMAGES');
      setGeneratingContentType('PRODUCT_IMAGES');
      setGeneratingContentId(imageContent.id);
      setGeneratingStrategyPipeline(null);
      setResumingContentGeneration(true);
      return;
    }

    const activeContent = product.contents.find(content => (
      content.status === 'GENERATING' || Boolean(contentPipelineState(content))
    ));
    if (!activeContent || dismissedContentTargetRef.current === activeContent.id) return;
    const state = contentPipelineState(activeContent);
    setGeneratingContent(activeContent.type);
    setGeneratingContentType(activeContent.type);
    setGeneratingContentId(activeContent.id);
    setGeneratingStrategyPipeline(state?.pipeline || pipelineForContentType(activeContent.type));
    setResumingContentGeneration(true);
  }, [product, loading, generatingKb, showInputPanel, generatingContentType]);

  // Handlers
  const handleCreateKb = async (name: string) => {
    if (!productId) return;
    // Explicit navigation takes over from automatic recovery on page entry.
    autoGenTriggered.current = true;
    setShowNewKbModal(false);
    setInputPanelMode('knowledge');
    setPendingContentGeneration(null);
    setIsFirstGeneration(false);
    setRestartKnowledgeGeneration(false);
    setKnowledgeGenerationError(null);
    setSourceChecks({});
    sourceCheckRequestsRef.current = {};
    setGeneratingKbId(null);
    setGeneratingKbName(name);

    const result = await checkPipelineInputs();
    if (result.hasInputs) {
      setInputPanelFields(result.fields);
      setInputPanelScreens(buildInputScreens(result.fields));
      setWizardStep(0);
      setWizardValues(buildInitialWizardValues(result.fields));
      setShowInputPanel(true);
    } else {
      setGeneratingKb(true);
    }
  };

  // Wizard navigation
  const handleWizardNext = () => {
    const activeScreens = visibleInputScreens(inputPanelScreens, wizardValues);
    if (wizardStep < activeScreens.length - 1) {
      setWizardStep(prev => prev + 1);
    } else {
      wizardHistoryPushed.current = false;
      if (typeof window !== 'undefined') {
        window.history.replaceState({ ...(window.history.state || {}), ecomflowLabWizard: false }, '', window.location.href);
      }
      const visibleFields = activeScreens.flatMap(screen => screen.fields);
      const visibleValues = Object.fromEntries(
        Object.entries(wizardValues).filter(([key]) => visibleFields.some(field => field.name === key)),
      );
      const normalizedValues = normalizeWizardValues(normalizeBriefEconomics(visibleValues, visibleFields.filter(field => field.inputType === 'packages').map(field => field.name)));
      setShowInputPanel(false);
      if (inputPanelMode === 'content' && pendingContentGeneration) {
        const pending = pendingContentGeneration;
        setPendingContentGeneration(null);
        void handleGenerateContent(pending.type, pending.kbId, {
          ...(pending.initialInputs || {}),
          ...normalizedValues,
        }, pending.strategy, pending.pipeline, pending.autoChain, pending.production, pending.structure, pending.selectedSchemas, pending.carouselSchema);
        return;
      }
      setUserInputs(normalizedValues);
      setSearchParams({}, { replace: true });
      setGeneratingKb(true);
    }
  };

  const handleWizardBack = () => {
    if (wizardStep > 0) {
      setWizardStep(prev => prev - 1);
    }
  };

  const closeInputPanel = () => {
    if (wizardHistoryPushed.current && typeof window !== 'undefined') {
      window.history.back();
      return;
    }
    setShowInputPanel(false);
    setPendingContentGeneration(null);
    setWizardStep(0);
  };

  const handleWizardValueChange = (value: string) => {
    const activeScreens = visibleInputScreens(inputPanelScreens, wizardValues);
    const fieldName = activeScreens[wizardStep]?.fields[0]?.name;
    if (fieldName) {
      setWizardValues(prev => ({ ...prev, [fieldName]: value }));
    }
  };

  const handleWizardFieldChange = (fieldName: string, value: string) => {
    setWizardValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleCloseKnowledgeGeneration = () => {
    // Saved checkpoints remain resumable, but must not immediately reopen
    // the panel that the user has just dismissed (also before refetch returns).
    autoGenTriggered.current = true;
    setGeneratingKb(false);
    setGeneratingKbId(null);
    setGeneratingKbName('');
    setResumingGeneration(false);
    setRestartKnowledgeGeneration(false);
    if (!product?.knowledgeBases.some(kb => kb.isComplete)) {
      navigate(product?.brandId
        ? `/marketing/baza-produktow?brandId=${encodeURIComponent(product.brandId)}`
        : '/marketing/baza-produktow?unbranded=true');
      return;
    }
    void loadProduct();
  };

  const handleGenerationComplete = async () => {
    await waitForCompleteKnowledgeBase();
    setGeneratingKb(false);
    setGeneratingKbId(null);
    setGeneratingKbName('');
    setUserInputs(undefined);
    setResumingGeneration(false);
    setRestartKnowledgeGeneration(false);
    setKnowledgeGenerationError(null);
  };

  const handleGenerationError = (msg: string) => {
    setGeneratingKb(false);
    setGeneratingKbId(null);
    setGeneratingKbName('');
    setResumingGeneration(false);
    setUserInputs(undefined);
    setRestartKnowledgeGeneration(false);
    setKnowledgeGenerationError(msg);
    console.error('KB generation error:', msg);
    loadProduct();
  };

  const handleRestartInterruptedKnowledge = async () => {
    if (!productId) throw new Error('Nie udało się ustalić produktu Laboratorium.');
    const incompleteKb = product?.knowledgeBases?.find(kb => !kb.isComplete);
    if (!incompleteKb) throw new Error('Przerwany szkic bazy wiedzy już nie istnieje.');
    const draftName = incompleteKb.name || generatingKbName || 'Główna Baza Wiedzy';

    await labApi.discardKnowledgeDraft(productId, incompleteKb.id);
    autoGenTriggered.current = true;
    setGeneratingKb(false);
    setResumingGeneration(false);
    setUserInputs(undefined);
    setKnowledgeGenerationError(null);
    await loadProduct();
    await startFirstKnowledgeBaseInstaller(draftName, true);
  };

  const retryKnowledgeGeneration = async (kbId?: string) => {
    const incompleteKb = product?.knowledgeBases?.find(kb => !kb.isComplete && (!kbId || kb.id === kbId));
    if (!productId || !incompleteKb) return;
    autoGenTriggered.current = true;
    setRetryingKnowledgeGeneration(true);
    setKnowledgeGenerationError(null);
    try {
      await labApi.generateKnowledge(productId, {
        kbId: incompleteKb.id,
        name: incompleteKb.name || 'Główna Baza Wiedzy',
      });
      setGeneratingKbId(incompleteKb.id);
      setGeneratingKbName(incompleteKb.name || 'Główna Baza Wiedzy');
      setRestartKnowledgeGeneration(false);
      setResumingGeneration(true);
      setGeneratingKb(true);
    } catch (error) {
      const message = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      setKnowledgeGenerationError(message || 'Nie udało się wznowić generowania bazy wiedzy.');
    } finally {
      setRetryingKnowledgeGeneration(false);
    }
  };

  const handleGenerateContent = async (type: string, kbId: string, contentInputs?: Record<string, string>, strategy?: string, strategyPipeline?: string, autoChain?: boolean, production?: { voiceId?: string; avatarId?: string; voiceSource?: 'heygen' | 'elevenlabs' } | null, structure?: string | null, selectedSchemas?: string[], carouselSchema?: string | null) => {
    if (!productId) return;
    dismissedContentTargetRef.current = null;
    setResumingContentGeneration(false);
    setKbPickerForContent(null);
    setGeneratingContent(type);
    setGeneratingContentType(type);
    setGeneratingStrategyPipeline(strategyPipeline || null);
    setGeneratingContentId(null); // Will be set after API response
    try {
      const res = await labApi.generateContent(productId, { type, strategy, structure: structure || undefined, selectedSchemas: selectedSchemas?.length ? selectedSchemas : undefined, carouselSchema: carouselSchema || undefined, autoChain, production: production || undefined, knowledgeBaseId: kbId, userInputs: contentInputs });
      const content = res.data.data;
      setGeneratingContentId(content.id);
    } catch (err) {
      console.error('Failed to start content generation:', err);
      setGeneratingContent(null);
      setGeneratingContentType(null);
    }
  };

  const prepareContentGeneration = async (type: string, kbId: string, initialInputs?: Record<string, string>, options?: { strategy?: string; structure?: string | null; selectedSchemas?: string[]; carouselSchema?: string | null; pipeline?: string; autoChain?: boolean; production?: { voiceId?: string; avatarId?: string; voiceSource?: 'heygen' | 'elevenlabs' } | null }) => {
    const pipeline = options?.pipeline || pipelineForContentType(type);
    const result = await checkPipelineInputs(pipeline);
    if (result.hasInputs) {
      setInputPanelMode('content');
      setPendingContentGeneration({ type, kbId, initialInputs, strategy: options?.strategy, structure: options?.structure, selectedSchemas: options?.selectedSchemas, carouselSchema: options?.carouselSchema, pipeline: options?.pipeline, autoChain: options?.autoChain, production: options?.production });
      setInputPanelFields(result.fields);
      setInputPanelScreens(buildInputScreens(result.fields, pipeline));
      setWizardStep(0);
      setWizardValues({
        ...buildInitialWizardValues(result.fields, initialInputs),
      });
      setShowInputPanel(true);
      return;
    }
    await handleGenerateContent(type, kbId, initialInputs, options?.strategy, options?.pipeline, options?.autoChain, options?.production, options?.structure, options?.selectedSchemas, options?.carouselSchema);
  };

  const handleGenerateProductPage = async (data: {
    mode: 'content' | 'combo' | 'gallery';
    knowledgeBaseId: string;
    labContentId?: string;
  }) => {
    if (!productId) return;
    dismissedContentTargetRef.current = null;
    setResumingContentGeneration(false);
    setGeneratingContent(data.mode === 'gallery' ? 'PRODUCT_IMAGES' : 'LANDING');
    setGeneratingContentType(data.mode === 'gallery' ? 'PRODUCT_IMAGES' : 'LANDING');
    setGeneratingContentId(null);

    try {
      const res = await labApi.generateProductPage(productId, data);
      const content = res.data.data;
      productPageModalHistoryPushed.current = false;
      if (typeof window !== 'undefined') {
        window.history.replaceState({ ...(window.history.state || {}), ecomflowLabProductPageModal: false }, '', window.location.href);
      }
      setShowProductPageGenerateModal(false);
      if (content?.id) {
        setGeneratingContentId(content.id);
      }
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Nie udało się rozpocząć generowania strony produktowej.';
      setGeneratingContent(null);
      setGeneratingContentType(null);
      setGeneratingContentId(null);
      throw new Error(message);
    }
  };

  const handleGenerateProductImages = async (data: {
    labContentId: string;
    knowledgeBaseId?: string;
    language: string;
    styleId?: string;
    quality?: 'low' | 'medium' | 'high';
    regenerateImageIds?: string[];
  }) => {
    if (!productId) return;
    dismissedContentTargetRef.current = null;
    setResumingContentGeneration(false);
    setGeneratingContent('PRODUCT_IMAGES');
    setGeneratingContentType('PRODUCT_IMAGES');
    setGeneratingContentId(data.labContentId);

    try {
      await labApi.generateProductImages(productId, data);
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Nie udało się rozpocząć generowania zdjęć produktowych.';
      setGeneratingContent(null);
      setGeneratingContentType(null);
      setGeneratingContentId(null);
      throw new Error(message);
    }
  };

  const handleContentGenerationComplete = async () => {
    // Refresh before releasing the panel; recovery must not reopen the same
    // run from the previous product snapshot or reuse its completion countdown.
    const refreshed = await loadProduct();
    if (!refreshed) return;
    setGeneratingContent(null);
    setGeneratingContentType(null);
    setGeneratingContentId(null);
    setGeneratingStrategyPipeline(null);
    setResumingContentGeneration(false);
    dismissedContentTargetRef.current = null;
  };

  const handleContentGenerationError = (msg: string) => {
    console.error('Content generation error:', msg);
    dismissedContentTargetRef.current = generatingContentId;
    setGeneratingContent(null);
    setGeneratingContentType(null);
    setGeneratingContentId(null);
    setGeneratingStrategyPipeline(null);
    setResumingContentGeneration(false);
    loadProduct();
  };

  const handleResumeContentGeneration = (content: LabContent) => {
    const recordedPipeline = content.sections?.__pipeline_state?.pipeline;
    dismissedContentTargetRef.current = null;
    setGeneratingContent(content.type);
    setGeneratingContentType(content.type);
    setGeneratingContentId(content.id);
    setGeneratingStrategyPipeline(recordedPipeline || pipelineForContentType(content.type));
    setResumingContentGeneration(true);
  };

  const handleCloseContentGeneration = () => {
    dismissedContentTargetRef.current = generatingContentId;
    setGeneratingContent(null);
    setGeneratingContentType(null);
    setGeneratingContentId(null);
    setGeneratingStrategyPipeline(null);
    setResumingContentGeneration(false);
    void loadProduct();
  };

  const handleNewContent = (type: string, initialInputs?: Record<string, string>) => {
    if (!product) return;
    const completedKbs = product.knowledgeBases.filter(kb => kb.isComplete);
    if (completedKbs.length === 0) return;
    if (type === 'LANDING') {
      setShowProductPageGenerateModal(true);
      return;
    }
    if (type === 'VIDEO_SCRIPT') {
      if (videoStrategies.length === 0) {
        void labApi.getVideoStrategies().then(res => {
          setVideoStrategies(res.data.data?.strategies || []);
          setVideoStructures(res.data.data?.videoStructures || []);
        }).catch(err => console.error('Failed to load video strategies:', err));
      }
      setVideoStrategyPicker({ kbIds: completedKbs.map(kb => kb.id) });
      return;
    }
    if (type === 'AD_GRAPHIC') {
      if (staticStrategies.length === 0) {
        void labApi.getStaticStrategies().then(res => {
          setStaticStrategies(res.data.data?.strategies || []);
          setStaticSchemas(res.data.data?.schemas || []);
          setStaticCategories(res.data.data?.categories || []);
        }).catch(err => console.error('Failed to load static strategies:', err));
      }
      setStaticStrategyPicker({ initialInputs });
      return;
    }
    if (type === 'SOCIAL_POST') {
      if (socialStrategies.length === 0) {
        void labApi.getSocialStrategies().then(res => {
          setSocialStrategies(res.data.data?.strategies || []);
        }).catch(err => console.error('Failed to load social strategies:', err));
      }
      setSocialStrategyPicker({ initialInputs });
      return;
    }
    if (completedKbs.length === 1) {
      void prepareContentGeneration(type, completedKbs[0].id, initialInputs);
    } else {
      setKbPickerForContent({ type, initialInputs });
    }
  };

  const handleSocialStrategySelected = (strategy: SocialStrategyOption) => {
    if (!product) return;
    const initialInputs = socialStrategyPicker?.initialInputs;
    setSocialStrategyPicker(null);
    const completedKbs = product.knowledgeBases.filter(kb => kb.isComplete);
    if (completedKbs.length === 0) return;
    // Legacy "Pojedyncze posty" = generacja bez strategii (pipeline typu SOCIAL_POST)
    const options = strategy.pipeline ? { strategy: strategy.key, pipeline: strategy.pipeline } : undefined;
    if (completedKbs.length === 1) {
      void prepareContentGeneration('SOCIAL_POST', completedKbs[0].id, initialInputs, options);
    } else {
      setKbPickerForContent({ type: 'SOCIAL_POST', initialInputs, ...(options || {}) });
    }
  };

  const handleStaticStrategySelected = (strategy: StaticStrategyOption, selection: StaticStrategySelection) => {
    if (!product) return;
    const initialInputs = staticStrategyPicker?.initialInputs;
    setStaticStrategyPicker(null);
    const completedKbs = product.knowledgeBases.filter(kb => kb.isComplete);
    if (completedKbs.length === 0) return;
    const options = {
      strategy: strategy.key,
      pipeline: strategy.pipeline,
      selectedSchemas: selection.selectedSchemas,
      carouselSchema: selection.carouselSchema,
    };
    if (completedKbs.length === 1) {
      void prepareContentGeneration('AD_GRAPHIC', completedKbs[0].id, initialInputs, options);
    } else {
      setKbPickerForContent({ type: 'AD_GRAPHIC', initialInputs, ...options });
    }
  };

  const handleVideoStrategySelected = (strategy: LabVideoStrategyOption, autoChain: boolean, production: { voiceId?: string; avatarId?: string; voiceSource?: 'heygen' | 'elevenlabs' } | null, structure?: string | null) => {
    if (!product) return;
    setVideoStrategyPicker(null);
    const completedKbs = product.knowledgeBases.filter(kb => kb.isComplete);
    if (completedKbs.length === 0) return;
    const options = { strategy: strategy.key, structure, pipeline: strategy.pipeline, autoChain, production };
    if (completedKbs.length === 1) {
      void prepareContentGeneration('VIDEO_SCRIPT', completedKbs[0].id, undefined, options);
    } else {
      setKbPickerForContent({ type: 'VIDEO_SCRIPT', ...options });
    }
  };

  const closeProductPageGenerateModal = () => {
    if (productPageModalHistoryPushed.current && typeof window !== 'undefined') {
      window.history.back();
      return;
    }
    setShowProductPageGenerateModal(false);
  };

  const handleDeleteKb = async (_kbId: string) => {
    if (!productId) return;
    setDeleteConfirm(null);
    try {
      await labApi.deleteKnowledge(productId);
      loadProduct();
    } catch (err) {
      console.error('Failed to delete KB:', err);
    }
  };

  const handleRemoveLaboratory = async () => {
    if (!productId) return;
    setRemovingLaboratory(true);
    setRemoveLaboratoryError(null);
    try {
      await labApi.removeLaboratory(productId);
      setRemoveLaboratoryConfirm(false);
      navigate(product?.brandId
        ? `/marketing/baza-produktow?brandId=${encodeURIComponent(product.brandId)}`
        : '/marketing/baza-produktow?unbranded=true');
    } catch (err) {
      console.error('Failed to remove laboratory:', err);
      const responseError = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : null;
      setRemoveLaboratoryError(responseError || 'Nie udało się usunąć Laboratorium. Spróbuj ponownie.');
    } finally {
      setRemovingLaboratory(false);
    }
  };

  const handleSaveProduct = async (data: Record<string, unknown>) => {
    if (!productId) return;
    const enumFields = new Set(['potential', 'testStatus', 'status']);
    const cleaned: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      cleaned[key] = enumFields.has(key) && value === '' ? null : value;
    }
    if (Array.isArray(cleaned.adTranscripts)) {
      cleaned.adTranscripts = (cleaned.adTranscripts as string[]).filter(item => item.trim());
    }
    if (Array.isArray(cleaned.pricePackages)) {
      cleaned.pricePackages = (cleaned.pricePackages as Array<{ name: string; oldPrice: string; newPrice: string }>)
        .filter(item => item.name.trim() || item.oldPrice.trim() || item.newPrice.trim());
    }
    await labApi.updateProduct(productId, cleaned);
    await loadProduct();
  };

  const handleDeleteContent = async (contentId: string) => {
    if (!productId) return;
    setDeleteConfirm(null);
    try {
      await labApi.deleteContent(productId, contentId);
      loadProduct();
    } catch (err) {
      console.error('Failed to delete content:', err);
    }
  };

  // Render
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
      </div>
    );
  }

  if (error || !product) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="Nie znaleziono produktu"
        description={error || 'Produkt nie istnieje lub został usunięty.'}
        buttonLabel="Wróć do Laboratorium"
        buttonTo="/marketing/laboratorium"
      />
    );
  }

  // Show wizard input panel
  if (showInputPanel && inputPanelFields.length > 0) {
    const activeScreens = visibleInputScreens(inputPanelScreens, wizardValues);
    const currentScreen = activeScreens[wizardStep] || { key: 'fallback', fields: [inputPanelFields[wizardStep]] };
    const configuredScreen = inputPanelScreens.find(screen => screen.key === currentScreen.key);
    const sourceCheckKeys = [...new Set((configuredScreen?.fields || currentScreen.fields).flatMap(field => (
      field.fallbackForSource
        ? [field.fallbackForSource]
        : field.inputType === 'link' && ['marketplace_links', 'advertorial', 'landing'].includes(field.name)
          ? [field.name]
          : []
    )))];
    return (
      <InputPanel
        fields={currentScreen.fields}
        values={wizardValues}
        onChange={handleWizardValueChange}
        onFieldChange={handleWizardFieldChange}
        stepIndex={wizardStep}
        totalSteps={activeScreens.length || inputPanelFields.length}
        onNext={handleWizardNext}
        onBack={wizardStep > 0 ? handleWizardBack : undefined}
        onClose={closeInputPanel}
        sourceChecks={inputPanelMode === 'knowledge' ? sourceChecks : undefined}
        sourceCheckKeys={inputPanelMode === 'knowledge' ? sourceCheckKeys : undefined}
      />
    );
  }

  // Show inline generation panel
  if (generatingKb && productId) {
    return (
      <GenerationPanel
        productId={productId}
        knowledgeBaseId={generatingKbId}
        resumeAlreadyStarted={Boolean(generatingKbId)}
        kbName={generatingKbName}
        isFirstGeneration={isFirstGeneration}
        userInputs={userInputs}
        restartKnowledge={restartKnowledgeGeneration}
        resuming={resumingGeneration}
        backLabel="Zamknij"
        onBack={handleCloseKnowledgeGeneration}
        onRestart={handleRestartInterruptedKnowledge}
        onComplete={handleGenerationComplete}
        onError={handleGenerationError}
      />
    );
  }

  // Show inline content generation panel
  if (generatingContentType && productId) {
    return (
      <GenerationPanel
        key={`content:${productId}:${generatingContentType}:${generatingContentId || 'new'}`}
        productId={productId}
        mode={generatingContentType}
        pipelineOverride={generatingStrategyPipeline || undefined}
        contentId={generatingContentId}
        kbName=""
        isFirstGeneration={false}
        resuming={resumingContentGeneration}
        onBack={handleCloseContentGeneration}
        onComplete={handleContentGenerationComplete}
        onError={handleContentGenerationError}
      />
    );
  }

  const hasCompleteKb = product.knowledgeBases.some(kb => kb.isComplete);

  if (showProductPageGenerateModal) {
    return (
      <ProductPageGenerateModal
        knowledgeBases={product.knowledgeBases}
        contents={product.contents}
        onClose={closeProductPageGenerateModal}
        onSubmit={handleGenerateProductPage}
      />
    );
  }

  // No completed KB: installer/resume effect above takes over, no intermediate page.
  if (!hasCompleteKb) {
    const incompleteKb = product.knowledgeBases?.find(kb => !kb.isComplete);
    const persistedError = getKnowledgePipelineFailure(incompleteKb);
    const visibleError = knowledgeGenerationError || persistedError;
    if (retryingKnowledgeGeneration) {
      return (
        <div className="flex min-h-[40vh] items-center justify-center gap-3 text-text-secondary">
          <Loader2 className="h-6 w-6 animate-spin text-accent-primary" />
          Wznawiam od ostatniego zapisanego kroku...
        </div>
      );
    }
    if (visibleError) {
      return (
        <EmptyState
          icon={AlertCircle}
          title="Nie udało się wygenerować bazy wiedzy"
          description={visibleError}
          iconColorClass="from-danger/20 to-warning/10 border-danger/20 text-danger"
          actions={(
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button onClick={() => void retryKnowledgeGeneration()} variant="primary" leftIcon={<RefreshCw />}>
                Spróbuj ponownie
              </Button>
              <Button onClick={() => navigate('/marketing/laboratorium')} variant="secondary">
                Wróć do Laboratorium
              </Button>
            </div>
          )}
        />
      );
    }
    if (incompleteKb && product.status === 'ANALYZING' && searchParams.get('autoGenerate') !== 'true') {
      return (
        <div className="flex items-center justify-center min-h-[40vh]">
          <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <Tabs value={dashboardTab} onValueChange={handleDashboardTabChange} className="space-y-6">
      <StickyHeader
        product={product}
        onBack={() => navigate('/marketing/laboratorium')}
        hasCompleteKb={hasCompleteKb}
        onGenerateAll={() => setShowGenerateAll(true)}
      />

      <TabsContent value="assets" className="mt-0">
        <ContentHub
          product={product}
          productId={productId!}
          hasCompleteKb={hasCompleteKb}
          generatingContent={generatingContent}
          onNewContent={handleNewContent}
          onNewKb={() => setShowNewKbModal(true)}
          onDeleteKb={(kbId) => setDeleteConfirm({ type: 'kb', id: kbId })}
          onDeleteContent={(contentId) => setDeleteConfirm({ type: 'content', id: contentId })}
          onResumeContent={handleResumeContentGeneration}
          onResumeKnowledge={retryKnowledgeGeneration}
          resumingKnowledge={retryingKnowledgeGeneration}
          knowledgeGenerationError={knowledgeGenerationError}
          onReload={loadProduct}
          onOpenBatchModal={() => setShowBatchModal(true)}
          onGenerateProductImages={handleGenerateProductImages}
          initialTabToken={contentHubTabToken}
          onTabTokenChange={handleContentHubTabTokenChange}
        />
      </TabsContent>

      <TabsContent value="radar" className="mt-0">
        <CompetitorSection
          productId={productId!}
          competitors={product.competitors || []}
          onReload={loadProduct}
        />
      </TabsContent>

      <TabsContent value="suppliers" className="mt-0">
        <SupplierSection
          productId={productId!}
          suppliers={product.suppliers || []}
          onReload={loadProduct}
        />
      </TabsContent>

      <TabsContent value="sources" className="mt-0">
        <ProductSourcesPanel
          product={product}
          fields={productFields}
          fieldOptions={productFieldOptions}
          onSave={handleSaveProduct}
          onRemoveLaboratory={() => {
            setRemoveLaboratoryError(null);
            setRemoveLaboratoryConfirm(true);
          }}
        />
      </TabsContent>

      {/* Modals */}
      {showNewKbModal && (
        <NewKbModal
          onConfirm={handleCreateKb}
          onClose={() => setShowNewKbModal(false)}
        />
      )}

      {videoStrategyPicker && (
        <VideoStrategyPickerModal
          strategies={videoStrategies}
          structures={videoStructures}
          onSelect={handleVideoStrategySelected}
          onClose={() => setVideoStrategyPicker(null)}
        />
      )}

      {staticStrategyPicker && (
        <StaticStrategyPickerModal
          strategies={staticStrategies}
          schemas={staticSchemas}
          categories={staticCategories}
          onSelect={handleStaticStrategySelected}
          onClose={() => setStaticStrategyPicker(null)}
        />
      )}

      {socialStrategyPicker && (
        <SocialStrategyPickerModal
          strategies={socialStrategies}
          onSelect={handleSocialStrategySelected}
          onClose={() => setSocialStrategyPicker(null)}
        />
      )}

      {kbPickerForContent && (
        <KbPickerModal
          knowledgeBases={product.knowledgeBases}
          onSelect={kbId => void prepareContentGeneration(kbPickerForContent.type, kbId, kbPickerForContent.initialInputs, { strategy: kbPickerForContent.strategy, structure: kbPickerForContent.structure, selectedSchemas: kbPickerForContent.selectedSchemas, carouselSchema: kbPickerForContent.carouselSchema, pipeline: kbPickerForContent.pipeline, autoChain: kbPickerForContent.autoChain, production: kbPickerForContent.production })}
          onClose={() => setKbPickerForContent(null)}
        />
      )}

      {deleteConfirm && (
        <DeleteConfirmModal
          title="Potwierdź usunięcie"
          message={`Czy na pewno chcesz usunąć ${deleteConfirm.type === 'kb' ? 'tę bazę wiedzy' : 'tę treść'}? Tej operacji nie można cofnąć.`}
          onConfirm={() => {
            if (deleteConfirm.type === 'kb') handleDeleteKb(deleteConfirm.id);
            else handleDeleteContent(deleteConfirm.id);
          }}
          onClose={() => setDeleteConfirm(null)}
        />
      )}

      {removeLaboratoryConfirm && (
        <DeleteConfirmModal
          title="Usuń Laboratorium produktu"
          message={`Usuniesz bazy wiedzy, wygenerowane treści i zasoby Laboratorium dla „${product.name}”. Karta produktu pozostanie w Bazie produktów, projekty Studio zostaną odłączone, a pliki na Google Drive pozostaną bez zmian. Tej operacji w EcomFlow nie można cofnąć.`}
          confirmLabel="Usuń Laboratorium"
          confirmationPhrase="USUŃ LABORATORIUM"
          busy={removingLaboratory}
          error={removeLaboratoryError}
          onConfirm={handleRemoveLaboratory}
          onClose={() => {
            if (removingLaboratory) return;
            setRemoveLaboratoryConfirm(false);
            setRemoveLaboratoryError(null);
          }}
        />
      )}

      {showGenerateAll && (
        <GenerateAllModal
          productId={productId!}
          hasCompleteKb={hasCompleteKb}
          onClose={() => setShowGenerateAll(false)}
          onStarted={() => {
            setShowGenerateAll(false);
            loadProduct();
          }}
        />
      )}

      {showBatchModal && (
        <BatchGenerateModal
          productId={productId!}
          onClose={() => setShowBatchModal(false)}
          onStarted={() => {
            setShowBatchModal(false);
            loadProduct();
          }}
        />
      )}

    </Tabs>
  );
}

// Sticky Header sub-component
function StickyHeader({
  product,
  onBack,
  hasCompleteKb,
  onGenerateAll,
}: {
  product: LabProduct;
  onBack: () => void;
  hasCompleteKb: boolean;
  onGenerateAll: () => void;
}) {
  return (
    <div className="sticky top-20 z-30 -mx-1 px-1 pb-0">
      <div className="bg-surface-base/95 backdrop-blur-sm border-b border-border-subtle -mx-5 px-5 py-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <button
              onClick={onBack}
              className="p-1.5 text-text-secondary hover:text-text-primary rounded-lg hover:bg-surface-hover transition-colors"
              aria-label="Wróć do listy produktów"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <h1 className="min-w-0 truncate text-lg font-bold text-text-primary">{product.name}</h1>
          </div>

          <div className="flex items-center gap-3 overflow-x-auto">
            <TabsList className="min-w-max shrink-0">
              <TabsTrigger value="assets" leftIcon={<Library />}>Zasoby</TabsTrigger>
              <TabsTrigger value="radar" leftIcon={<Target />}>Radar</TabsTrigger>
              <TabsTrigger value="suppliers" leftIcon={<Package />}>Dostawcy</TabsTrigger>
              <TabsTrigger value="sources" leftIcon={<ClipboardList />}>Źródła</TabsTrigger>
            </TabsList>
            <Button
              onClick={onGenerateAll}
              disabled={!hasCompleteKb}
              variant="primary"
              size="md"
              leftIcon={<Rocket />}
              className="h-[44px] shrink-0 whitespace-nowrap px-5"
              title="Tryb YOLO"
            >
              Tryb YOLO
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
